import React from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { findMainStepForSlug, visibleSubSteps } from "../domain/steps";
import { useCoClient } from "../data/store";
import { cn } from "./cn";
import { CoButton } from "./Button";

/**
 * One row: where the CA is, and how far through.
 *
 *   Create New Company › Client basics › Company details        [Page 1 of 20]
 *   Jim Store › Verify setup › GST Settings                     [Page 9 of 20]
 *
 * The root crumb carries the page's own context rather than a separate line
 * above the title. On Company details that is the docx's "Create New Company"
 * / "Update Company" — which is what this screen IS, so saying it as the crumb
 * says it once instead of three times. Everywhere else the root is the client,
 * which is the thing the rest of the trail hangs off.
 *
 * The count is over `visibleSubSteps(client)`, not the raw catalog, so a client
 * whose path skips steps (Tally, compliance-only, no books connection) is
 * numbered 1..M with no gaps and no phantom pages. On the full Zoho path the
 * numbers match product's page numbering exactly.
 *
 * The route already names the client and the step, so this reads them itself
 * rather than making all twenty call sites pass what they already implied.
 * Renders nothing at all when either is unresolvable — a shell used outside
 * the wizard route keeps working.
 *
 * Nothing here is a link. Every earlier segment is a GROUP rather than a page,
 * so there is no single step a click could mean, and the left nav already
 * navigates — a crumb that looked clickable and only sometimes went somewhere
 * useful would be worse than plain text.
 */
function Breadcrumb() {
  const { clientId, step } = useParams<{ clientId: string; step: string }>();
  const client = useCoClient(clientId ?? "");

  if (!client || !step) return null;

  const subs = visibleSubSteps(client);
  const index = subs.findIndex((sub) => sub.slug === step);
  if (index === -1) return null;

  const group = findMainStepForSlug(client, step);
  // A draft that has not become a company yet is a creation; once `companyId`
  // is set the same screen is an edit of a real client.
  const root =
    step === "company-details"
      ? client.companyId === null
        ? "Create New Company"
        : "Update Company"
      : client.name || "New client";

  const trail = [root, group?.title, subs[index].title].filter(Boolean) as string[];

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      <p className="text-xs font-medium">
        {trail.map((segment, position) => (
          <React.Fragment key={`${segment}-${position}`}>
            {position > 0 && <span className="mx-1.5 text-muted-foreground/60">›</span>}
            <span className={position === trail.length - 1 ? "text-foreground" : "text-muted-foreground"}>
              {segment}
            </span>
          </React.Fragment>
        ))}
      </p>
      <span className="shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
        Page {index + 1} of {subs.length}
      </span>
    </div>
  );
}

export interface SubStepShellProps {
  title: string;
  description?: ReactNode;
  /** Answers "then what?" beside Continue. */
  thenWhat?: string;
  /** Renders the thenWhat line after the button row instead of before it. */
  thenWhatBelow?: boolean;
  children: ReactNode;
  onContinue: () => void;
  continueLabel?: string;
  continueDisabled?: boolean;
  className?: string;
}

// The middle-panel chrome: title/description, the sub-step's own body, then
// a footer with the "then what?" line and a single primary action.
export function SubStepShell({
  title,
  description,
  thenWhat,
  thenWhatBelow,
  children,
  onContinue,
  continueLabel = "Continue",
  continueDisabled,
  className,
}: SubStepShellProps) {
  const navigate = useNavigate();
  const thenWhatLine = thenWhat ? <p className="text-sm text-muted-foreground">{thenWhat}</p> : null;

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-col gap-1">
        <Breadcrumb />
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>

      <div className="flex flex-col gap-4">{children}</div>

      <div className="flex flex-col gap-3 border-t pt-6">
        {!thenWhatBelow && thenWhatLine}
        <div className="flex items-center justify-between gap-2">
          <CoButton variant="outline" className="w-fit" onClick={() => navigate("/admin/dashboard")}>
            Save and come back later
          </CoButton>
          <CoButton onClick={onContinue} disabled={continueDisabled} className="w-fit">
            {continueLabel}
          </CoButton>
        </div>
        {thenWhatBelow && thenWhatLine}
      </div>
    </div>
  );
}
